import { flatMap } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useDelayedAction, useMobile } from "@krea/customer-features/hooks";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import LayoutOneColumnHeader from "@krea/customer-features/shared-components/app/one-column/LayoutOneColumnHeader";
import AppQuestionsPreview from "@krea/customer-features/shared-components/application-questions-preview/AppQuestionsPreview";
import Button from "@krea/customer-features/shared-components/button";
import LoanApplicationFlowDescription from "@krea/customer-features/shared-components/loan-application-flow-description";
import { SubmitApplicationModal } from "@krea/customer-features/shared-components/modals";
import { useIsFieldRequestsSubmitting } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetCachedLenderResponses } from "@krea/customer-features/store/lender-responses/hooks/useGetCachedLenderResponses";
import { useGetUserData } from "@krea/customer-features/store/user/hooks/queries/useGetUserData";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";
import { LOAN_APPLICATION_STATES_WITH_DISABLED_PREVIEW_EDIT } from "@krea/customer-features/utils";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

import {
  useSubmitApplication,
  useWithoutAdvice,
} from "store/current-loan-application/hooks/mutations";
import { useGetCurrentLoanApplication } from "store/current-loan-application/hooks/queries";

export const ApplicationQuestionsPreview = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const navigate = useNavigate();

  const isFieldRequestSubmitting = useIsFieldRequestsSubmitting();
  const delayAction = useDelayedAction(isFieldRequestSubmitting);

  const { data: { isAdmin } = {} } = useGetUserData();
  const { data: loanApplication, refetch } = useGetCurrentLoanApplication();
  const { state, previewFields } = loanApplication ?? {};
  const { mutate: withoutAdvice } = useWithoutAdvice();
  const { mutateAsync: submitApplication } = useSubmitApplication();
  const [isOpen, setIsOpen] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const disableEdit =
    state &&
    LOAN_APPLICATION_STATES_WITH_DISABLED_PREVIEW_EDIT.includes(state) &&
    !isAdmin;

  const onSubmitApplication = async () => {
    if (state === LoanApplicationStates.DRAFT) {
      setIsOpen(true);
      await submitApplication();
      const { data: freshData } = await refetch();

      if (freshData?.state === LoanApplicationStates.ADVICE) {
        setIsDone(true);
      } else {
        navigate("/");
      }
    } else {
      delayAction(() => navigate("/"));
    }
  };

  const submitToLenders = () => {
    withoutAdvice(undefined, {
      onSuccess: () => {
        navigate("/");
      },
    });
  };
  const { loanApplicationID } = useParams();
  const { data: lenderResponsesData = {} } =
    useGetCachedLenderResponses(loanApplicationID);
  const hasBids = flatMap(lenderResponsesData).length > 0;

  let ctaText;

  switch (state) {
    case LoanApplicationStates.DRAFT:
      ctaText = t("iaq.applicationPreview.submitCta");
      break;

    case LoanApplicationStates.CREATED:
    case LoanApplicationStates.ADVICE:
      ctaText = t("iaq.applicationPreview.continueAndReceiveOffersCta");
      break;

    case LoanApplicationStates.OPEN_FOR_BIDS:
      if (hasBids) {
        ctaText = t("iaq.applicationPreview.showOffersCta");
      } else {
        ctaText = t("iaq.applicationPreview.backCta");
      }
      break;

    default:
      ctaText = t("iaq.applicationPreview.backToOffersCta");
  }

  return (
    <LayoutOneColumn
      aside={
        <div
          className={twMerge({
            ["tw-w-full tw-px-6 tw-bg-white"]: isMobile,
          })}
          style={
            isMobile
              ? {
                  zIndex: "100000",
                  position: "fixed",
                  left: "0",
                  bottom: "0",
                  height: "100px",
                  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.10)",
                }
              : { display: "flex", justifyContent: "center" }
          }
        >
          <Button
            block
            size="lg"
            tabIndex={0}
            className={twMerge("tw-text-center", {
              "tw-w-full tw-mt-6 tw-mx-auto tw-max-w-[590px] md:tw-max-w-[720px] lg:tw-max-w-[750px]":
                isMobile,
              "tw-w-full": !isMobile,
            })}
            onClick={() => {
              onSubmitApplication();
            }}
          >
            {ctaText}
          </Button>
        </div>
      }
    >
      <LayoutOneColumnHeader
        title={
          state === LoanApplicationStates.DRAFT
            ? t("iaq.applicationPreview.heading")
            : t("iaq.applicationPreview.nonDraft.heading")
        }
      >
        {!isMobile && (
          <Button
            id="submit-application"
            onClick={() => {
              onSubmitApplication();
            }}
          >
            {ctaText}
          </Button>
        )}
      </LayoutOneColumnHeader>

      <div
        className="tw-my-6 tw-px-6 tw-py-4"
        style={{ border: "5px solid #fbf2ff", borderRadius: "8px" }}
      >
        <LoanApplicationFlowDescription />
      </div>

      <AppQuestionsPreview
        previewFields={previewFields}
        disableEdit={disableEdit}
      />
      <SubmitApplicationModal
        isDone={isDone}
        open={isOpen}
        setOpen={setIsOpen}
        goToAdvice={() => navigate("/direct/advice")}
        submitToLenders={submitToLenders}
      />
    </LayoutOneColumn>
  );
};
